import {
  Box,
  Button,
  Collapse,
  IconButton,
  makeStyles,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import StarRoundedIcon from '@material-ui/icons/StarRounded';
import { DateFormat } from 'config';
import { differenceInCalendarYears, format, isValid, parseISO } from 'date-fns';
import { SimilarCandidateResult } from 'lib/Model/SimilarCandidates';
import * as React from 'react';
import { CandidateMetadataChips } from './CandidateMetadataChips';
import { Feedback, FeedbackControls } from './FeedbackControls';
import { ProfileSummary } from './ProfileSummary';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    backgroundColor: '#FFFFFF',
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(1),
    alignItems: 'start',
    // Stretched to a row-mate's height (parent grid): keep rows packed at the
    // top so the slack collects as one clean gap at the bottom, not split
    // between the meta and feedback rows.
    alignContent: 'start',
    willChange: 'transform',
  },
  badge: {
    width: 30,
    height: 30,
    borderRadius: 15,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(44, 6, 3, 0.06)',
    color: theme.palette.primary.main,
  },
  rank: {
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
  },
  strongTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    backgroundColor: theme.palette.primary.main,
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    padding: '1px 6px 1px 4px',
    borderRadius: 3,
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
  },
  strongTagIcon: {
    fontSize: 12,
  },
  body: {
    minWidth: 0,
  },
  topLine: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  role: {
    fontWeight: 700,
  },
  id: {
    color: theme.palette.text.secondary,
    fontFamily: 'monospace',
    fontSize: 12,
  },
  candidateLinkButton: {
    padding: 2,
    color: theme.palette.text.secondary,
    verticalAlign: 'middle',
  },
  candidateLinkIcon: {
    fontSize: 15,
  },
  candidateLinkLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  meta: {
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  matchReason: {
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(1.25),
    borderLeft: `3px solid ${theme.palette.secondary.main}`,
    color: theme.palette.text.primary,
    fontStyle: 'italic',
  },
  scoreCol: {
    textAlign: 'right',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 2,
  },
  scoreValue: {
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1,
    color: theme.palette.primary.main,
  },
  scoreLabel: {
    fontSize: 10,
    color: theme.palette.text.secondary,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  expandBtn: {
    minWidth: 0,
    padding: theme.spacing(0.25, 0),
    color: theme.palette.primary.main,
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  expandIcon: {
    transition: 'transform 200ms ease',
    fontSize: 18,
  },
  expandIconOpen: {
    transform: 'rotate(180deg)',
  },
  feedbackRow: {
    gridColumn: '2 / span 2',
  },
  detailsRow: {
    gridColumn: '2 / span 2',
  },
  detailsBox: {
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: '#FAFAFA',
    borderRadius: 4,
    border: '1px solid #ECECEC',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  subScores: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  subScore: {
    display: 'flex',
    flexDirection: 'column',
  },
  subScoreLabel: {
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
  },
  subScoreValue: {
    fontSize: 14,
    fontWeight: 600,
  },
  tutorialCardLift: {
    position: 'relative',
    zIndex: 1301,
  },
  tutorialFocus: {
    position: 'relative',
    zIndex: 1302,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#FFFFFF',
    boxShadow: `0 0 0 4px #FFFFFF, 0 0 0 8px ${theme.palette.primary.main}`,
  },
}));

interface Props {
  rank: number;
  isStrong: boolean;
  result: SimilarCandidateResult;
  finalScore: number;
  feedback: Feedback | undefined;
  onFeedbackChange: (next: Feedback | undefined) => void;
  // the ats link was opened
  onOpen?: () => void;
  cardRef?: (el: HTMLDivElement | null) => void;
  defaultExpanded?: boolean;
  tutorialFocus?: 'card' | 'score' | 'details' | 'reason' | 'feedback';
}

function candidateName(result: SimilarCandidateResult): string {
  const name = [result.first_name, result.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  return name || `Candidate ${result.candidate_id}`;
}

export function formatLatestActivityDate(value?: string | null): string | null {
  if (!value) return null;

  const parsed = parseISO(value);
  if (!isValid(parsed)) return null;

  return `Last activity: ${format(parsed, DateFormat.SHORT)}`;
}

export function formatCvDate(value?: string | null): string | null {
  if (!value) return null;

  const parsed = parseISO(value);
  if (!isValid(parsed)) return null;

  // Spell out the CV's age so staleness registers at a glance — recruiters
  // repeatedly marked otherwise-good matches bad for being "an old profile".
  // Age is informational only: old CVs are not penalised in ranking.
  const years = differenceInCalendarYears(new Date(), parsed);
  const age =
    years >= 1 ? ` (${years} ${years === 1 ? 'year' : 'years'} old)` : '';
  return `Last CV: ${format(parsed, DateFormat.SHORT)}${age}`;
}

function candidateMeta(result: SimilarCandidateResult): string {
  const details = [
    formatCvDate(result.cv_date),
    formatLatestActivityDate(result.latest_activity_date),
  ]
    .filter(Boolean)
    .join(' - ');
  return details;
}

function makeRecruiterFriendly(text?: string | null): string {
  if (!text) return '';

  return text
    .replace(/\bIC\b/g, 'Hands-on Professsional')
    .replace(/\bscope\b/gi, 'Level of responsibility')
    .replace(/\bseniority\b/gi, 'Experience level');
}

// the applications tab, where the internal referral is created
function getAtsCandidateUrl(candidateId: string): string {
  return `https://konnekt.talexiohr.com/ats/candidates/${candidateId}#applications`;
}

export const ResultCard: React.FC<Props> = ({
  rank,
  isStrong,
  result,
  finalScore,
  feedback,
  onFeedbackChange,
  onOpen,
  cardRef,
  defaultExpanded = false,
  tutorialFocus,
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  // Only a real AI-generated match reason belongs in the reason slot. When the
  // re-ranking didn't cover this candidate (or failed for the whole search),
  // show nothing — falling back to the profile preview here made a CV dump
  // masquerade as reasoning. The preview stays available under Details.
  const reason = result.reranked
    ? makeRecruiterFriendly(result.llm_reason)
    : '';
  const profilePreview = makeRecruiterFriendly(result.profile_preview);

  return (
    <Paper
      ref={cardRef}
      elevation={0}
      variant="outlined"
      className={`${classes.card} ${
        tutorialFocus === 'card' ? classes.tutorialFocus : ''
      } ${
        tutorialFocus && tutorialFocus !== 'card'
          ? classes.tutorialCardLift
          : ''
      }`}
      data-tutorial="result-card"
    >
      <Box className={classes.badge} aria-label={`Rank ${rank}`}>
        <Typography component="span" className={classes.rank}>
          {rank}
        </Typography>
      </Box>

      <Box className={classes.body}>
        <Box className={classes.topLine}>
          <Typography variant="subtitle1" className={classes.role}>
            {candidateName(result)}
          </Typography>
          <Box className={classes.candidateLinkLabel}>
            <Tooltip title="Open candidate in ATS">
              <IconButton
                component="a"
                href={getAtsCandidateUrl(result.candidate_id)}
                target="_blank"
                onClick={onOpen}
                // middle click opens a tab too, right click doesn't count
                onAuxClick={(e: React.MouseEvent) => {
                  if (e.button === 1) onOpen?.();
                }}
                rel="noopener noreferrer"
                size="small"
                className={classes.candidateLinkButton}
                aria-label={`Open candidate ${result.candidate_id} in ATS`}
              >
                <OpenInNewIcon className={classes.candidateLinkIcon} />
              </IconButton>
            </Tooltip>
          </Box>
          {isStrong && (
            <Tooltip title="Stands clearly above the rest on match score">
              <Box component="span" className={classes.strongTag}>
                <StarRoundedIcon className={classes.strongTagIcon} />
                Strongest
              </Box>
            </Tooltip>
          )}
        </Box>
        <Typography variant="body2" className={classes.meta}>
          {candidateMeta(result)}
        </Typography>
        <CandidateMetadataChips
          country={result.country}
          hexaco={result.hexaco}
          salary={result.salary}
          isNli={result.is_nli}
          tcnTypeId={result.tcn_type_id}
          flagMissingCountry
        />
        {reason && (
          <Typography
            variant="body2"
            className={`${classes.matchReason} ${
              tutorialFocus === 'reason' ? classes.tutorialFocus : ''
            }`}
            data-tutorial="result-reason"
          >
            {reason}
          </Typography>
        )}
      </Box>

      <Box
        className={`${classes.scoreCol} ${
          tutorialFocus === 'score' ? classes.tutorialFocus : ''
        }`}
        data-tutorial="result-score"
      >
        <Typography className={classes.scoreValue}>
          {(finalScore * 100).toFixed(0)}
        </Typography>
        <Typography className={classes.scoreLabel}>Match</Typography>
        <Button
          className={classes.expandBtn}
          size="small"
          onClick={() => setExpanded((x) => !x)}
          endIcon={
            <ExpandMoreIcon
              className={`${classes.expandIcon} ${
                expanded ? classes.expandIconOpen : ''
              }`}
            />
          }
          aria-expanded={expanded}
        >
          Details
        </Button>
      </Box>

      <Box
        className={`${classes.feedbackRow} ${
          tutorialFocus === 'feedback' ? classes.tutorialFocus : ''
        }`}
        data-tutorial="result-feedback"
      >
        <FeedbackControls feedback={feedback} onChange={onFeedbackChange} />
      </Box>

      <Box className={classes.detailsRow}>
        <Collapse in={expanded} unmountOnExit>
          <Box
            className={`${classes.detailsBox} ${
              tutorialFocus === 'details' ? classes.tutorialFocus : ''
            }`}
            data-tutorial="result-details"
          >
            <Box className={classes.subScores}>
              <Box className={classes.subScore}>
                <Typography className={classes.subScoreLabel}>
                  Similarity
                </Typography>
                <Typography className={classes.subScoreValue}>
                  {(result.vector_score * 100).toFixed(0)}%
                </Typography>
              </Box>
              <Box className={classes.subScore}>
                <Typography className={classes.subScoreLabel}>
                  Overall match
                </Typography>
                <Typography className={classes.subScoreValue}>
                  {(finalScore * 100).toFixed(0)}%
                </Typography>
              </Box>
            </Box>
            {result.profile_preview && <ProfileSummary text={profilePreview} />}
          </Box>
        </Collapse>
      </Box>
    </Paper>
  );
};
